import React, { useState, useEffect } from "react";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import authApi from "./auth-api";
import { userActions, apiList, methodType } from "./constant";
import getToken from "../util/tokenGenerator";
import { stripHtmlTags, handleTokenRequest, decodeJWT } from "../util/helper";
import handleWindowPopUp from "../util/handleWindowPopup";
import { env } from "../cfg/env";
import { extractLanguageFromPathname } from "../util/helper";
import DateFormatter from "../util/dateFormatter";

const AppStateContext = React.createContext();

const initiateLogoutAndInformMiddlware = async ({
  updateAppState,
  instance,
  accounts,
  token,
}) => {
  const decodedToken = decodeJWT(token);
  const email = decodedToken?.email;
  const homeAccountId = accounts[0]?.homeAccountId ?? "";
  const logoutRequest = {
    account: instance.getAccountByHomeId(homeAccountId),
    postLogoutRedirectUri: `${window.location.origin}${window.location.pathname}`,
  };
  const urlLang = extractLanguageFromPathname(window.location.pathname);
  const daysToPersist = 365;
  const expiry = DateFormatter.getDateinFuture(daysToPersist * 86400000);
  const domain = /local/.test(window.location.host)
    ? "localhost"
    : "moneris.com";
  updateAppState({
    type: userActions.middlewareLogout,
    body: { email },
    postCallHandler: async () => {
      window.sessionStorage.removeItem("token");
      window.sessionStorage.removeItem("tokenExpiry");
      window.localStorage.removeItem("activeApp");
      document.cookie = `lang=${urlLang}; expires=${expiry};domain=${domain};path=/`;
      window.localStorage.setItem("lang", urlLang);
      instance.logoutRedirect(logoutRequest);
    },
  });
};

const getData = async ({
  body = {},
  url = "",
  method = methodType.post,
  isWithCredentials = true,
  tokenhandler = () => null,
  isSecured = false,
}) => {
  let response = await authApi({
    body,
    method,
    url,
    isWithCredentials,
    tokenhandler,
    isSecured,
  });
  return response;
};

function AppStateProvider({ children }) {
  const [appState, setAppState] = useState({
    isError: false,
    isPopUpBlocked: window.sessionStorage.getItem("isPopUpBlocked") === "true",
    tokenExpiry: null,
    token: null,
    isLoggingOut: false,
    isLoading: true,
    isAccountingEnabled: null,
  });

  const { instance, inProgress, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const origin = "settings";
  const getTokenHandler = () => getToken(instance, inProgress, accounts);
  const searchParams = new URLSearchParams(window?.location?.search) ?? "";
  const logoutAction = searchParams?.get("logout") ?? "";

  useEffect(async () => {
    let token = window.sessionStorage.getItem("token") ?? null;

    if (isAuthenticated) {
      if (token === null) {
        try {
          const silentRequest = {
            scopes: [
              `https://${env.sso.b2cTenantDomain}.onmicrosoft.com/sso/auth`,
            ],
            account: accounts[0],
          };
          const response = await instance.acquireTokenSilent(silentRequest);
          token = response.idToken || response.accessToken;

          window.sessionStorage.setItem("token", token);
        } catch (error) {
          console.log(
            "Silent token acquisition failed, user might need to login:",
            error,
          );
          return;
        }
      }

      if (logoutAction === "true") {
        await initiateLogoutAndInformMiddlware({
          updateAppState,
          instance,
          accounts,
          token,
        });
        // Break out of logout flow
        return;
      }

      // Redirect to UP Portal if conditions are met based on token
      const decodedToken = decodeJWT(token);
      if (decodedToken) {
        const apps = decodedToken?.appScope
          ? decodedToken?.appScope.split(",")
          : [];
        const isUPInApps = apps.find((code) => code === "UP");
        const redirectToUP = !!isUPInApps && logoutAction !== "true";

        if (redirectToUP) {
          const url = env.sso.UPPortalUrl;
          const isRedirected = handleWindowPopUp(url?.toString(), "_self");

          if (!isRedirected) {
            setAppState({ isPopUpBlocked: true });
            window.sessionStorage.setItem("isPopUpBlocked", true);
          }
          return;
        }
      }

      // If no logout or UP redirection proceed regular flow
      const myAccounts = instance.getAllAccounts();
      const accountObj = myAccounts[0] ?? {};
      const { idTokenClaims = {} } = accountObj;
      // todo enable after integeration
      const {
        uid = "",
        env: userEnv = "qa",
        isMigrationFlow = false,
      } = idTokenClaims;
      updateAppStateLocal({
        uid: uid,
        env: userEnv,
        isMigrationFlow: isMigrationFlow,
        isLoading: true,
        isError: false,
        isMd2Lite: false,
        isObSetup: false,
      });

      await getAccountingState(token);

      if (uid) {
        await getUserData(uid);
      }
    }
  }, [logoutAction, isAuthenticated]);

  const getAccountingState = async (token) => {
    const url = apiList[userActions.accountingFeatureFlag];
    const response = await authApi({
      body: {},
      method: methodType.get,
      url,
      tokenhandler: async () => token,
      isSecured: true,
    });

    const accountingState = response?.data;
    updateAppStateLocal({
      isAccountingEnabled: accountingState?.enabled ?? false,
    });
  };

  const getUserData = async (uid) => {
    const url = apiList[userActions.fetchUserInfo];
    const response = await getData({
      body: { origin, user_id: uid },
      method: methodType.post,
      url,
      tokenhandler: async () => await handleTokenRequest(getTokenHandler),
      isSecured: true,
    });
    let isRedirect = false;
    const { user = "", success = false } = response?.data ?? {};
    if (success) {
      const {
        apps,
        first_name = "",
        last_name = "",
        mobile_phone = "",
        email = "",
        display_name = "",
        first_sign_in: firstSignIn = false,
        mfa = {},
        preferred_language = "en",
        contact_phone = "",
        contact_phone_ext = "",
        password_last_change_date = null,
        password_exp_date = null,
        questions_last_change_date = null,
        hasPartnerRole = null,
        hasSharedCashRole = null,
        isMfaEnabled = false,
      } = user;

      //sync existing app lang
      const lang = window.localStorage.getItem("lang");
      await updateAppState({
        body: { preferred_language: lang },
        type: userActions.updateUserInfo,
        uid,
      });

      window.localStorage.setItem(
        "lang",
        String(preferred_language).toLowerCase(),
      );

      const isMd2LiteUserResponse = await getData({
        body: { user_id: uid, email: email },
        method: methodType.post,
        url: apiList[userActions.isMd2Lite],
        tokenhandler: async () => await handleTokenRequest(getTokenHandler),
        isSecured: true,
      });

      const isObSetupResponse = await getData({
        body: { user_id: uid, email },
        method: methodType.post,
        url: apiList[userActions.isObSetup],
        tokenhandler: async () => await handleTokenRequest(getTokenHandler),
        isSecured: true,
      });

      const isMd2LiteUser = isMd2LiteUserResponse?.data?.success ?? false;
      const isObSetupUser =
        (isObSetupResponse?.data?.success ?? false) &&
        (isObSetupResponse?.data?.isEligible ?? false);
      const hasMd2Tile = apps.reduce((prevVal, currentVal) => {
        const { app_code = "" } = currentVal;
        return prevVal || app_code?.toString().toLowerCase() === "md2";
      }, false);
      const hasUpCode = apps.reduce((prevVal, currentVal) => {
        const { app_code = "" } = currentVal;
        return prevVal || app_code?.toString().toLowerCase() === "UP";
      }, false);
      const { app_code = "" } = apps[0] ?? {};
      // isRedirect is to determine uis user is landing on sso page
      // true means no sso landing page to show
      isRedirect =
        isMd2LiteUser ||
        hasPartnerRole ||
        hasSharedCashRole ||
        hasUpCode ||
        (apps.length === 1 && app_code?.toString().toLowerCase() === "de") ||
        (isObSetupUser && hasMd2Tile) ||
        logoutAction === "true";

      updateAppStateLocal({
        uid: uid,
        isLoading: isRedirect,
        preferred_language,
        isMd2Lite: isMd2LiteUser,
        isObSetup: isObSetupUser,
        isMfaEnabled,
        profile: {
          firstName: stripHtmlTags(first_name),
          lastName: stripHtmlTags(last_name),
          displayName: display_name,
          businessPhone: contact_phone,
          userPhone: mobile_phone,
          userEmail: email,
          businessExtension: contact_phone_ext,
          firstSignIn,
          mfa: mfa,
          passLastChange: password_last_change_date,
          passExpiry: password_exp_date,
          secQuestionlastUpdate: questions_last_change_date,
        },
        apps,
        hasPartnerRole,
        hasSharedCashRole,
      });
    } else {
      updateAppStateLocal({
        uid: uid,
        isLoading: isRedirect,
      });
    }
  };

  const updateAppState = async (payload) => {
    const {
      type = "",
      body = {},
      uid: uidNew = "",
      postCallHandler = () => null,
    } = payload;
    const { uid = uidNew } = appState;
    let url = "";
    let response = {};
    switch (type) {
      case userActions.fetchUserInfo: {
        url = apiList[userActions.fetchUserInfo];
        response = await getData({
          body: { ...body, user_id: uid, origin },
          method: methodType.post,
          url,
          tokenhandler: async () => await handleTokenRequest(getTokenHandler),
          isSecured: true,
        });
        const { user = "", success = false } = response?.data ?? {};
        if (success) {
          const {
            first_name = "",
            last_name = "",
            mobile_phone = "",
            email = "",
            display_name = "",
            mfa = {},
            preferred_language = "en",
            contact_phone = "",
            contact_phone_ext = "",
            password_last_change_date = null,
            password_exp_date = null,
            questions_last_change_date = null,
            hasPartnerRole,
            hasSharedCashRole,
            isMfaEnabled = false,
          } = user;
          window.localStorage.setItem(
            "lang",
            String(preferred_language).toLowerCase(),
          );
          updateAppStateLocal({
            preferred_language,
            isLoading: false,
            hasPartnerRole,
            hasSharedCashRole,
            isMfaEnabled,
            profile: {
              firstName: stripHtmlTags(first_name),
              lastName: stripHtmlTags(last_name),
              displayName: display_name,
              businessPhone: contact_phone,
              userPhone: mobile_phone,
              userEmail: email,
              businessExtension: contact_phone_ext,
              mfa: mfa,
              passLastChange: password_last_change_date,
              passExpiry: password_exp_date,
              secQuestionlastUpdate: questions_last_change_date,
            },
          });
        } else {
          updateAppStateLocal({
            isLoading: false,
          });
        }
        break;
      }
      case userActions.getAppIds: {
        const myAccounts = instance.getAllAccounts() ?? [];
        const accountObj = myAccounts[0] ?? {};
        const { name = "" } = accountObj;
        updateAppStateLocal({ firstName: stripHtmlTags(name) });
        break;
      }
      case userActions.updateUserInfo: {
        url = apiList[userActions.updateUserInfo];
        updateAppStateLocal({ isLoading: true });
        response = await getData({
          body: { ...body, user_id: uid, origin },
          method: methodType.post,
          url,
          tokenhandler: async () => await handleTokenRequest(getTokenHandler),
          isSecured: true,
        });
        if (response?.data?.success) {
          console.log("lang synced successful");
        } else {
          console.log("lang sync unsuccessful");
        }

        break;
      }
      case userActions.middlewareLogout: {
        const url = apiList[userActions.middlewareLogout];
        const { email = "" } = body;
        updateAppStateLocal({
          isLoading: true,
        });
        response = await getData({
          body: {},
          method: methodType.get,
          url: `${url}?email=${email}`,
          isWithCredentials: false,
        });
        if (response?.status === 200) {
          console.log("logout successful");
          postCallHandler();
          updateAppStateLocal({
            isLoading: false,
          });
        } else {
          console.log("error logging out user");
          postCallHandler();
          updateAppStateLocal({
            isLoading: false,
          });
        }

        break;
      }
      default:
        break;
    }
  };

  const updateAppStateLocal = (obj = null) => {
    if (!obj) {
      return;
    }
    // If obj is an object with isLoading property and logoutAction is defined, wait for logout action to complete
    if (
      typeof obj === "object" &&
      obj?.hasOwnProperty("isLoading") &&
      !obj.isLoading &&
      logoutAction
    ) {
      console.log(
        "Waiting for logout action to complete before updating app state",
      );
      setTimeout(() => {
        setAppState((prevAppState) => ({ ...prevAppState, ...obj }));
      }, 7000);
    } else {
      setAppState((prevAppState) => ({ ...prevAppState, ...obj }));
    }
  };

  const value = { appState, updateAppState, updateAppStateLocal, getUserData };
  return (
    <AppStateContext.Provider value={value}>
      {children}
    </AppStateContext.Provider>
  );
}

function useAppStateProvider() {
  const context = React.useContext(AppStateContext);
  if (context === undefined) {
    throw new Error("appContext must be used within a AppStateProvider");
  }
  return context;
}

export { AppStateProvider, AppStateContext, useAppStateProvider };
